// @flow
import * as React from "react"
import { connect } from "react-redux"
import classnames from "classnames"
import Portal from "@kiwicom/orbit-components/lib/Portal"

import throttle from "common/utils/throttle"
import { getWidth } from "../../../../app/scripts/tools/windowBindings"
import { getLanguageInfo } from "../../services/staticData"

const DEFAULT_ELEMENT_ID = "detached"
const ANIMATED_STYLE_PROPS = ["opacity", "background"]

type Style = { [string]: * }

type Connect = {|
  isRtl: boolean,
|}

type Props = Connect & {|
  children: React.Node,
  elementID?: string,
  zIndex?: number | string,
  className?: string,
  preservePosition?: boolean,
  animated?: boolean,
  style?: Style,
  dataTest?: string,
  key?: string,
|}

type State = {|
  style: Style,
|}

const getAnimatedStyle = ({ animated, style }: Props) =>
  animated
    ? {
        opacity: style && style.opacity,
        background: "rgba(0, 0, 0, .5)",
      }
    : {}

class MoveToBody extends React.PureComponent<Props, State> {
  static defaultProps = {
    elementID: DEFAULT_ELEMENT_ID,
    className: "",
  }

  constructor(props: Props) {
    super(props)

    if (typeof window !== "undefined") {
      const elementID = props.elementID || DEFAULT_ELEMENT_ID // flow is stupid
      const node = document.getElementById(elementID)

      if (!node) {
        const newNode = document.createElement("div")
        newNode.setAttribute("id", elementID)

        if (document.body) {
          document.body.appendChild(newNode)
        }
      }
    }

    this.state = {
      style: this.getStyle(),
    }
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const { style } = state

    const newAnimatedStyle = getAnimatedStyle(props)
    const styleChanged = ANIMATED_STYLE_PROPS.some(p => style[p] !== newAnimatedStyle[p])

    if (!styleChanged) {
      return null
    }

    return {
      style: { ...style, ...newAnimatedStyle },
    }
  }

  componentDidMount() {
    if (this.props.preservePosition) {
      window.addEventListener("scroll", this.updatePosition)
      window.addEventListener("resize", this.updatePosition)

      const libraryEl = document.getElementById("library")

      if (libraryEl) {
        libraryEl.addEventListener("scroll", this.updatePosition)
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.updatePosition)
    window.removeEventListener("resize", this.updatePosition)

    const libraryEl = document.getElementById("library")

    if (libraryEl) {
      libraryEl.removeEventListener("scroll", this.updatePosition)
    }
  }

  getStyle = () => ({
    ...this.getPositionStyle(),
    ...getAnimatedStyle(this.props),
  })

  getPositionStyle = () => {
    const { preservePosition, isRtl } = this.props
    const { placeholderRef } = this

    if (!preservePosition || !placeholderRef || !(placeholderRef instanceof HTMLDivElement)) {
      return {}
    }

    const rect = placeholderRef.getBoundingClientRect()

    return {
      position: "absolute",
      top: rect.top,
      ...(isRtl ? { right: getWidth() - rect.left } : { left: rect.left }),
    }
  }

  storePlaceholderRef = (el: ?HTMLDivElement) => {
    const changed = el !== this.placeholderRef

    this.placeholderRef = el

    if (changed) {
      this.setState({ style: this.getStyle() })
    }
  }

  updatePosition = throttle(10, () => {
    this.setState({ style: this.getStyle() })
  })

  placeholderRef: ?HTMLDivElement = null

  render() {
    const {
      elementID,
      dataTest,
      className,
      animated,
      children,
      key,
      style: propsStyle,
    } = this.props
    const { style } = this.state

    return (
      typeof window !== "undefined" && (
        <>
          <div
            className="MoveToBody"
            style={{ position: "absolute", left: 0 }}
            ref={this.storePlaceholderRef}
            key={key}
          />
          <Portal element={elementID}>
            <div
              className={classnames(className, "spBodyElement", { _animated: animated })}
              style={{ zIndex: propsStyle && propsStyle.zIndex }}
            >
              <div style={style} className="spBodyElement-inner" data-test={dataTest}>
                {children}
              </div>
            </div>
          </Portal>
        </>
      )
    )
  }
}

// $FlowFixMe TODO: plz add type when updating
export default connect(state => ({
  isRtl: getLanguageInfo(state).direction === "rtl",
}))(MoveToBody)
